import React, { memo, useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Input, Popover } from '@lyearn/ui';
import { KeyCodes } from '@/consts/keyCodes';
import { TextTagType, TextTagTypeEnum } from '@/types/schema';

import { TextValue } from '../../types';
import ActionButton from './ActionButton';
import { inputStyle, longTextStyle, shortTextStyle } from './styles';
import { TagsValueSelectorProps } from './types';

const onKeyDown =
  (onEnter: () => void) => (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === KeyCodes.Enter) {
      if (!e.shiftKey) {
        onEnter();
        e.preventDefault();
      }
    }
  };

function TextSelect(props: TagsValueSelectorProps) {
  const { t } = useTranslation();
  const { className, tag, selectedTagValue, onChange, onDelete, error, readOnly, children } = props;
  const textTag = tag as TextTagType;
  const textTagValue = (selectedTagValue || '') as TextValue;
  const [inputVal, setInputVal] = useState(textTagValue || '');
  const onInputChange = useCallback(
    (val: string) => {
      onChange(val);
    },
    [onChange],
  );
  const onBlur = useCallback(() => {
    onInputChange(inputVal);
  }, [onInputChange, inputVal]);

  const isLongTextTag = textTag.textType === TextTagTypeEnum.Long;

  const ActionButtonComp = children || (
    <ActionButton
      className={className}
      defaultPlaceholder={t('Tags:Form.TypeValue')}
      error={error}
      readOnly={readOnly}
      selectedTagValue={textTagValue}
      tag={tag}
      onDelete={onDelete}
    />
  );

  return (
    <Box className={cn('max-w-full', className)}>
      <Popover>
        <Popover.Button className={cn('max-w-full', className)}>{ActionButtonComp}</Popover.Button>
        <Popover.Content className={cn('max-w-full')} position="bottom-start">
          <Input
            autoFocus
            className={cn(
              'w-full bg-bg-primary',
              inputStyle,
              isLongTextTag ? longTextStyle : shortTextStyle,
              className,
            )}
            multiline={isLongTextTag}
            placeholder={t('Tags:Form.TypeValue')}
            value={inputVal}
            onBlur={onBlur}
            onChange={(e) => setInputVal(e.currentTarget.value)}
            onKeyDown={onKeyDown(onBlur)}
          />
        </Popover.Content>
      </Popover>
    </Box>
  );
}

export default memo(TextSelect);
